// Pure mapping from a Better Auth `error` query-string code (as landed on
// `/login/error?error=...&error_description=...`) to plain-English copy for
// AuthError.jsx. No DOM/browser APIs here so it stays unit-testable in
// isolation from the page that renders it.
//
// Shape returned for every code: { title, body, action }.
// - title: short heading (rendered as the page's single h1 via AuthLayout).
// - body: the plain-English explanation (rendered with role="alert").
// - action: a short suggested next step, shown under the body. The page
//   always also renders a generic "Back to sign in" link separately — this
//   is a hint, not the only way back.

const KNOWN_ERRORS = {
  signup_disabled: {
    title: 'Invitation only',
    body: 'This platform is invitation-only. Open the link in your invitation email to create your account.',
    action: 'Check your invitation email, then come back and sign in.',
  },
  account_not_linked: {
    title: 'Account already exists',
    body: "An account with this email already exists. Sign in with your password (or reset it), verify your email if you haven't, then try Google/Microsoft again.",
    action: 'Sign in with your password, or reset it.',
  },
  email_not_verified: {
    title: 'Verify your email',
    body: "Your email address hasn't been verified yet. Check your inbox for the verification link, then try again.",
    action: "Didn't get it? Ask us to resend the verification email from the sign-in page.",
  },
  email_not_found: {
    title: 'No account found',
    body: "We couldn't find an account with that email address.",
    action: 'Check the email you used, or open your invitation email if you were invited.',
  },
  access_denied: {
    title: 'Sign-in cancelled',
    body: 'You cancelled at Google/Microsoft.',
    action: 'Try again when you’re ready.',
  },
  oauth_provider_not_found: {
    title: 'Sign-in method not available',
    body: "That sign-in method isn't set up for this account.",
    action: 'Try signing in with your email and password instead.',
  },
  internal_server_error: {
    title: 'Something went wrong',
    body: 'Something went wrong on our end while signing you in.',
    action: 'Please try again in a moment.',
  },
  // IC-008 — organisation SSO. invalid_state / invalid_request / invalid_provider /
  // discovery_failed are the actual codes @better-auth/sso's OIDC callback handler appends to its
  // errorCallbackURL redirect (confirmed against node_modules/@better-auth/sso/dist/index.mjs —
  // see the IC-008 worksheet, §5). provider_not_found / domain_not_verified are not redirect
  // codes — the API returns them as a plain 404/401 to the initial sign-in request, which the
  // Login page handles inline rather than routing here — but are mapped anyway for consistency
  // and in case a future caller lands here with one.
  invalid_state: {
    title: 'Sign-in link expired',
    body: "That single sign-on attempt is no longer valid — it may have expired, already been used, or been opened in a different browser tab from where it started.",
    action: 'Go back to the sign-in page and start again.',
  },
  invalid_request: {
    title: 'Sign-in problem',
    body: 'Your organisation\'s identity provider sent back a response we could not process.',
    action: 'Try again, or contact your IT team if this keeps happening.',
  },
  invalid_provider: {
    title: 'Single sign-on unavailable',
    body: "Your organisation's single sign-on could not be completed — it may have been changed or removed while you were signing in.",
    action: 'Try again in a moment, or sign in with your email and password.',
  },
  discovery_failed: {
    title: 'Identity provider unreachable',
    body: "We couldn't reach your organisation's identity provider to complete sign-in.",
    action: 'Try again shortly, or contact your IT team if this keeps happening.',
  },
  provider_not_found: {
    title: 'Single sign-on not set up',
    body: "Your organisation hasn't set up single sign-on (SSO) yet.",
    action: 'Sign in with your email and password instead, or ask your organisation admin to set up SSO in Settings.',
  },
  domain_not_verified: {
    title: 'Single sign-on not yet active',
    body: "Your organisation's single sign-on is registered but its domain hasn't been verified yet.",
    action: 'Ask your organisation admin to complete domain verification in Settings, or sign in with your email and password.',
  },
};

/**
 * @param {string | null | undefined} code - the `error` query param.
 * @param {string | null | undefined} [_description] - the `error_description`
 *   query param. Not currently interpolated into copy (kept plain-English and
 *   stable rather than echoing provider-supplied text), but accepted so
 *   callers can pass it through without a branch, and so future codes can use
 *   it without changing the call site.
 * @returns {{ title: string, body: string, action: string }}
 */
export function getAuthErrorInfo(code, _description) {
  if (code && KNOWN_ERRORS[code]) return KNOWN_ERRORS[code];
  return {
    title: 'Sign-in problem',
    body: code
      ? `We couldn't sign you in (code: ${code}).`
      : "We couldn't sign you in.",
    action: 'Go back and try again, or use your email and password.',
  };
}

export default getAuthErrorInfo;
